import React, {useEffect} from 'react';
import {Box, Button, Paper} from '@mui/material';
import {useApolloClient} from '@apollo/client';
import {useStaffPermissions} from './useStaffPermissions';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {
  GlobalPermissionEnum,
  PermGroupsEnum,
  UserPermissionEnum,
} from '../../schema-types/globalTypes';
import Loading from '../../components/Loading/Loading';
import {StaffDetails} from '../../schema-types/StaffDetails';
import {STAFF_DETAILS} from '../../graphql/fragments';
import withAutoFormNew, {FormReadOnly} from '../../components/AutoForm/AutoFormNew';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {
  additionalPermissionsField,
  globalPermissionsField,
  groupNameField,
  permissionsFieldValues,
} from './fields';
import {useDjangoContext} from '../../contexts/useAuthContext';

interface AdminValues {
  groupName?: PermGroupsEnum;
  permissions?: (UserPermissionEnum | null)[] | null;
  featurePermissions: GlobalPermissionEnum[];
}

export const AdminDetailPanelContent: React.FC<{staff: StaffDetails}> = ({staff}) => {
  const {id, userPerms, groupPerms, groups} = staff;
  const {getDefault, updatePermissions, permissionsLoading, updatedStaff, changeGroup} =
    useStaffPermissions();
  const initialValues: AdminValues = {
    groupName: groups?.[0]?.name as PermGroupsEnum,
    permissions: (userPerms
      ?.filter((x) => x?.codename && permissionsFieldValues.includes(x.codename))
      .map((x) => x?.codename) || []) as unknown as UserPermissionEnum[],
    featurePermissions:
      groupPerms
        ?.filter((x) => x?.codename && !permissionsFieldValues.includes(x.codename))
        .map((x) => x!.codename!) ||
      [] ||
      [],
  };
  const {cache} = useApolloClient();

  const updateStaffFragment = async () => {
    const x = await updatedStaff({
      variables: {
        staffId: id,
      },
    });
    const thisStaff = x.data?.staff?.[0];
    if (!thisStaff) {
      return;
    }
    cache.writeFragment({
      data: thisStaff,
      fragmentName: 'StaffDetails',
      fragment: STAFF_DETAILS,
      id: `StaffType:${id}`,
    });
  };

  if (permissionsLoading) {
    return (
      <Paper>
        <Loading />
      </Paper>
    );
  }

  return (
    <PermissionsFormWrapped
      formFields={[]}
      id={id}
      updateStaffFragment={updateStaffFragment}
      initialValueOverrides={initialValues}
      submit={(submittedValues) => {
        const permissions: (UserPermissionEnum | null)[] =
          submittedValues.permissions || [];
        updatePermissions({
          variables: {
            staffId: id,
            permissions,
          },
        }).then(() => window.location.reload());
      }}
    />
  );
};

type permissionFormProps = {
  updateStaffFragment: () => Promise<void>;
  id: string;
};

export const RestoreButton: React.FC = () => {
  const {getDefault} = useStaffPermissions();

  const {djangoUser} = useDjangoContext();
  return (
    <Button
      variant="outlined"
      color="secondary"
      onClick={() => {
        getDefault({
          variables: {
            staffId: djangoUser.id,
          },
          fetchPolicy: 'network-only',
        }).then(() => window.location.reload());
      }}
    >
      Restore my default permissions
    </Button>
  );
};

const PermissionsForm: React.FC<permissionFormProps> = ({updateStaffFragment, id}) => {
  const {getDefault, changeGroup} = useStaffPermissions();
  const {handleSubmit, values, initialValues} = useAutoForm<AdminValues>();
  useEffect(() => {
    if (initialValues.groupName === values.groupName) {
      return;
    }
    const group = values.groupName as PermGroupsEnum;
    changeGroup({
      variables: {
        staffId: id,
        group,
      },
    }).then(() => window.location.reload());
  }, [values.groupName]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Box bgcolor="white" p={2}>
          <Box mr={3}>
            <AutoField field={groupNameField} />
          </Box>
          <Box mr={3} mt={2}>
            <FormReadOnly readOnly>
              <AutoField field={globalPermissionsField} />
            </FormReadOnly>
          </Box>
          <Box mr={3} mt={2}>
            <AutoField field={additionalPermissionsField} />
          </Box>
          <Box mt={3} mb={3}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{marginRight: '2rem'}}
            >
              Apply changes
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                getDefault({
                  variables: {
                    staffId: id,
                  },
                  fetchPolicy: 'network-only',
                }).then(() => window.location.reload());
              }}
            >
              Sync user permissions to Curo
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

const PermissionsFormWrapped = withAutoFormNew<AdminValues, permissionFormProps>(
  PermissionsForm
);
